import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowOverflowWhenOpen", "autoFocus", "children", "className", "contentId", "contentTag", "disabled", "eagerRender", "embedded", "fwdRef", "headerTag", "indented", "initiallyOpen", "onChangeEnd", "onClose", "onOpen", "size", "title", "wrapContent"];
import React, { forwardRef, useState, useRef, useImperativeHandle, useEffect, useContext, useMemo } from 'react';
import SmoothCollapse from 'react-smooth-collapse';
import { v4 as uuidv4 } from 'uuid';
import { deprecated } from '@op/opds.opux';
import { IconCollapse } from '@op/opds.opux-icons/navigation';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { ExpanderListContext } from './expander-list';
const noop = () => undefined;

/**
 * _Expanders are used to reveal and hide a panel of content related to its header._
 *
 * ***
 *
 * The component consists of a header with a title and an associated
 * panel of content. The content can be expanded and collapsed.
 * The props control for example the look and the semantics of the
 * component. Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 *
 * Exposes imperative methods `open()`, `close()`.
 */
export const Expander = _ref => {
  let {
      allowOverflowWhenOpen = false,
      autoFocus = false,
      children,
      className,
      contentId: contentIdProp,
      contentTag: contentTagProp,
      disabled = false,
      eagerRender = false,
      embedded: embeddedProp = false,
      fwdRef,
      headerTag: headerTagProp,
      indented: indentedProp = false,
      initiallyOpen = false,
      onChangeEnd,
      onClose,
      onOpen,
      size: sizeProp = null,
      title,
      wrapContent = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (autoFocus !== false) {
    deprecated('The autoFocus of the Expander is deprecated as of August 2024');
  }
  const [contentIdState] = useState(() => "ds-expander-content-" + uuidv4());
  const [open, setOpen] = useState(!!initiallyOpen);
  // Correct syntax found from this thread: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35572
  const smoothCollapseRef = useRef();
  const expanderRef = useRef(null);
  const {
    accordion,
    contentTag: contentTagContext,
    current,
    embedded: embeddedContext,
    headerTag: headerTagContext,
    indented: indentedContext,
    onClose: onCloseContext,
    onOpen: onOpenContext,
    registerItem,
    size: sizeContext,
    toggleExpander
  } = useContext(ExpanderListContext);
  const contentId = contentIdProp || contentIdState;
  const ContentTag = contentTagProp || contentTagContext || 'div';
  const HeaderTag = headerTagProp || headerTagContext || 'div';
  const embedded = embeddedProp || embeddedContext;
  const indented = indentedProp || indentedContext;
  const size = sizeProp != null ? sizeProp : sizeContext;
  const uniqueExpanderId = useMemo(() => registerItem == null ? void 0 : registerItem(), []);
  useEffect(() => {
    if (accordion && current !== uniqueExpanderId) {
      setOpen(false);
    }
  }, [current]);
  useEffect(() => {
    if (accordion && initiallyOpen) {
      setOpen(true);
      toggleExpander == null || toggleExpander(uniqueExpanderId);
    }
  }, []);
  useEffect(() => {
    if (onOpen && open) {
      onOpen();
    }
    if (onClose && !open) {
      onClose();
    }
    if (onOpenContext && open) {
      onOpenContext(uniqueExpanderId);
    }
    if (onCloseContext && !open) {
      onCloseContext(uniqueExpanderId);
    }
    if (open && accordion) {
      toggleExpander == null || toggleExpander(uniqueExpanderId);
    }
  }, [open]);
  function handleOpenEnd() {
    if (onChangeEnd) {
      onChangeEnd();
    }
    if (open && autoFocus) {
      var _smoothCollapseRef$cu, _contentRef$current;
      // Using "as any" to suppress error "Property '_main' does not exist on type 'SmoothCollapse'""
      const contentRef = (_smoothCollapseRef$cu = smoothCollapseRef.current) == null ? void 0 : _smoothCollapseRef$cu._main;
      const input = contentRef == null || (_contentRef$current = contentRef.current) == null ? void 0 : _contentRef$current.querySelector('button, input:not([type="hidden"]), select, textarea');
      if (input) {
        input.focus();
      }
    }
  }
  function handleKeyDown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      setOpen(currentOpen => !currentOpen);
    }
  }
  useImperativeHandle(fwdRef, () => ({
    close: () => setOpen(false),
    expanderRef,
    open: () => setOpen(true)
  }));
  const cn = buildVersionedClassNames('ds-expander', className, {
    'ds-expander--disabled': disabled,
    'ds-expander--embedded': embedded,
    'ds-expander--indented': indented,
    'ds-expander--open': open,
    'ds-expander--small': size && size === 'small',
    'ds-expander--tiny': size && size === 'tiny'
  });
  const contentClassName = buildVersionedClassNames('ds-expander__content', {
    'ds-expander__content--embedded': embedded,
    'ds-expander__content--indented': indented,
    'ds-expander__content--unwrapped': !wrapContent
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn,
    ref: expanderRef
  }, props), /*#__PURE__*/React.createElement(HeaderTag, {
    className: buildVersionedClass('ds-expander__header'),
    onClick: event => {
      event.preventDefault();
      if (!disabled) {
        setOpen(currentOpen => !currentOpen);
      }
    },
    onKeyDown: event => {
      if (!disabled) {
        handleKeyDown(event);
      }
    }
  }, /*#__PURE__*/React.createElement("button", {
    "aria-controls": contentId,
    "aria-expanded": open,
    className: buildVersionedClass('ds-expander__title'),
    disabled: disabled
  }, title), /*#__PURE__*/React.createElement(IconCollapse, {
    className: buildVersionedClass('ds-expander__header-action'),
    color: !disabled ? 'action' : 'disabled'
  })), /*#__PURE__*/React.createElement(ContentTag, null, /*#__PURE__*/React.createElement(SmoothCollapse, {
    allowOverflowWhenOpen: allowOverflowWhenOpen,
    className: contentClassName,
    expanded: open,
    id: contentId,
    onChangeEnd: handleOpenEnd,
    ref: smoothCollapseRef,
    eagerRender: eagerRender
  }, /*#__PURE__*/React.createElement(ExpanderListContext.Provider, {
    value: {
      contentTag: contentTagContext,
      embedded: embeddedContext,
      headerTag: headerTagContext,
      indented: indentedContext,
      size: sizeContext,
      // Do not pass accordion related properties to nested expanders
      accordion: false,
      current: undefined,
      onClose: noop,
      onOpen: noop,
      registerItem: noop,
      toggleExpander: noop
    }
  }, children))));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Expander, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Expander';
export default Fwd;